import type { Editor } from '@tiptap/react';
import type {
  DocumentEditFinishedEvent,
  DocumentEditStartEvent,
} from '../data/tracking.ts';

type TrackedEvent = DocumentEditStartEvent | DocumentEditFinishedEvent;

export class DocumentEditingEventsRecorder {
  readonly mountedTimestamp: number;
  documentEditStart: number | null = null;
  blurTimeoutId: ReturnType<typeof setTimeout> | null = null;
  readonly blurTimeoutInterval = 1000;

  constructor(
    private recordEvent: (event: TrackedEvent) => void,
    private editor: Editor,
  ) {
    this.mountedTimestamp = Math.floor(Date.now() / 1000);
    this.editor.on('update', this.handleEditorChange);
    this.editor.on('blur', this.handleEditorBlurWithDelay);
    this.editor.on('focus', this.handleEditorFocus);
    this.editor.on('destroy', this.handleEditorDestroy);
  }

  private handleEditorChange = () => {
    if (this.documentEditStart === null) {
      this.documentEditStart = Math.floor(Date.now() / 1000);

      const event: DocumentEditStartEvent = {
        name: 'DocumentEditStart',
        documentEditStart: this.documentEditStart,
        documentLength: this.editor.getText().length,
        secondsSinceVisible: this.mountedTimestamp,
      };

      this.recordEvent(event);
    }
  };

  private handleEditorBlurWithDelay = () => {
    if (this.blurTimeoutId) {
      clearTimeout(this.blurTimeoutId);
    }
    this.blurTimeoutId = setTimeout(
      this.handleEditorBlur,
      this.blurTimeoutInterval,
    );
  };

  private handleEditorFocus = () => {
    if (this.blurTimeoutId) {
      clearTimeout(this.blurTimeoutId);
      this.blurTimeoutId = null;
    }
  };

  private handleEditorBlur = () => {
    if (this.documentEditStart !== null) {
      const finishedTimestamp = Math.floor(Date.now() / 1000);

      const event: DocumentEditFinishedEvent = {
        name: 'DocumentEditFinished',
        documentEditFinished: finishedTimestamp,
        documentEditingDurationSeconds:
          finishedTimestamp - this.documentEditStart,
        documentLength: this.editor.getText().length,
      };

      this.recordEvent(event);
      this.documentEditStart = null;
    }
    this.blurTimeoutId = null;
  };

  private handleEditorDestroy = () => {
    this.handleEditorBlur();
  };
}
